/**
 * Stop an ad that is already on screen from being repainted when a later ad
 * mounts.
 *
 * WHY. The deployed widget bundle
 * (`widget.sellwild.com/sellwild/sellwild-sellwild-next.js`,
 * last-modified 2026-07-31) ends every auction with:
 *
 *     swpbjs.setTargetingForGPTAsync(swpbjs.gamAdUnitCodes);
 *     googletag.pubads().refresh(swpbjs.gamSlots);   // the WHOLE array
 *
 * and starts an auction from every `<sellwild-ad>` insertion. So one new ad
 * entering the DOM re-requests and re-renders every slot registered on the page,
 * filled or not — each one visibly blanking and repainting. On an infinite feed
 * that fires again on every scroll batch, which is the flashing readers see.
 *
 * The widget already has the real fix: `011a2467` on master ("improve refresh
 * logic in view") gates each unit on `firstLoadedAdUnitCodes` and viewability so
 * an insertion only requests units that never loaded. It is not in the deployed
 * bundle because CI compiles only the widgets whose own `widgets/*.md` changed,
 * so a push touching `src/AdStack.tsx` publishes nothing. Until that ships, this
 * reproduces the first-load-only half of it from our side, where we can deploy.
 *
 * HOW. `googletag` is a page global and `pubads()` is a singleton, so we wrap
 * `refresh` once: slots that have rendered a non-empty creative are filtered out
 * of every refresh, and a refresh left with nothing to do is dropped entirely.
 *
 * A slot is marked when it RENDERS, not when it is refreshed — which is where
 * this deliberately differs from upstream's `markFirstLoaded(codesToMark)`. It
 * matters because the widget disables GPT's initial load on this site
 * (`initializeGoogleCMP` runs `pubads().disableInitialLoad()`, reached because
 * `widgets/sellwild-sellwild-next.md` sets `GPP_ENABLED: false` with an
 * `ADSENSE_CLIENT_ID`), so `googletag.display()` only registers a slot and every
 * first paint arrives through `refresh`. Marking on render therefore cannot
 * starve a slot: it has never rendered before its first refresh, so that one
 * always passes, and a slot that comes back unfilled stays eligible for the
 * next auction. Marking on the refresh call instead would spend a slot's single
 * chance on a round that may have returned nothing.
 *
 * Nothing of value is lost while this is in place. Timed refresh is already off
 * for sellwild — `widgets/sellwild-sellwild-next.md` sets no `AD_REFRESH_MAX`,
 * so the bundle's `refreshMax` compiles to 0 and its 30s re-auction timer never
 * arms. Every refresh reaching us today is the incidental all-slots one. It also
 * protects revenue: `setTargetingForGPTAsync` CLEARS the previous round's `hb_*`
 * keys before writing the current round's, so a re-auction that no-bids replaces
 * a high-CPM fill with remnant — see ./ad-insert-batch for the same argument.
 *
 * REMOVE THIS once the widget bundle is rebuilt off master. If the widget team
 * deliberately enables refresh before then it would be suppressed silently, so
 * `window.__SW_DISABLE_AD_REFRESH_GUARD__ = true` before load turns it off
 * without a deploy.
 */

import { isAdSlotAttached, onAdSlotsReaped, reapDetachedAdSlots } from './ad-slot-reaper';

interface GptSlot {
  getSlotElementId?: () => string;
}

interface GptPubads {
  refresh: (slots?: GptSlot[], options?: unknown) => unknown;
  getSlots?: () => GptSlot[];
  addEventListener?: (event: string, handler: (e: unknown) => void) => void;
  __swRefreshGuarded?: boolean;
}

interface SlotRenderEndedEvent {
  slot?: GptSlot;
  isEmpty?: boolean;
}

/**
 * Slots holding a creative. Membership is for the life of the document, not the
 * page view: a client-side navigation removes the element but leaves its GAM
 * slot in `swpbjs.gamSlots` (the widget has no disconnect handler), so a slot
 * forgotten on navigation would start being refreshed again from off-screen.
 */
const filled = new Set<GptSlot>();

// A destroyed slot is gone for good; holding it here would keep it alive.
onAdSlotsReaped((slots) => slots.forEach((slot) => filled.delete(slot)));

let installed = false;
let suppressed = 0;

function guard(pubads: GptPubads): void {
  if (pubads.__swRefreshGuarded) return;
  pubads.__swRefreshGuarded = true;

  pubads.addEventListener?.('slotRenderEnded', (event: unknown) => {
    const { slot, isEmpty } = (event ?? {}) as SlotRenderEndedEvent;
    // An empty slot is still worth re-requesting — there is nothing on screen
    // for a repaint to disturb, and the next auction may fill it.
    if (!slot || isEmpty) return;
    filled.add(slot);
  });

  const originalRefresh = pubads.refresh.bind(pubads);

  pubads.refresh = (slots?: GptSlot[], options?: unknown) => {
    const requested = Array.isArray(slots)
      ? slots
      : typeof pubads.getSlots === 'function'
        ? pubads.getSlots()
        : null;

    // A refresh whose slots we cannot enumerate is passed through untouched
    // rather than guessed at.
    if (!requested) return originalRefresh(slots, options);

    // Every slot passes through here on its way to its first paint, so this is
    // where the reaper sees each one attached -- and where a slot whose element
    // has since left the page is destroyed instead of re-requested.
    reapDetachedAdSlots();
    const live = requested.filter(isAdSlotAttached);

    const unfilled = live.filter((slot) => !filled.has(slot));
    suppressed += live.length - unfilled.length;

    if (unfilled.length === 0) return undefined;
    return originalRefresh(unfilled, options);
  };
}

/**
 * Install the guard. Idempotent, and safe to call before GPT has loaded — the
 * work is queued on `googletag.cmd`, which GPT drains in order once it does.
 */
export function installAdRefreshGuard(): void {
  if (typeof window === 'undefined' || installed) return;

  const w = window as unknown as {
    googletag?: { cmd?: unknown[]; pubads?: () => GptPubads };
    __SW_DISABLE_AD_REFRESH_GUARD__?: boolean;
  };

  if (w.__SW_DISABLE_AD_REFRESH_GUARD__) return;
  installed = true;

  // Same bootstrap the widget's own slot scripts use, so whichever runs first
  // creates the queue and the other appends to it.
  w.googletag = w.googletag || {};
  w.googletag.cmd = w.googletag.cmd || [];
  (w.googletag.cmd as (() => void)[]).push(() => {
    const pubads = w.googletag?.pubads?.();
    if (pubads) guard(pubads);
  });
}

/** Slots currently holding a creative — for tests and diagnostics. */
export function filledAdSlotCount(): number {
  return filled.size;
}

/** Slot refreshes dropped so far — for tests and diagnostics. */
export function suppressedAdRefreshCount(): number {
  return suppressed;
}

/** Tests only. The guard is installed once per document in the app. */
export function resetAdRefreshGuard(): void {
  filled.clear();
  installed = false;
  suppressed = 0;
}
